import * as React from 'react';
import Box from '@mui/material/Box';
import Scrollbar from '../components/scrollbar';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { WarningOutlined } from '@mui/icons-material';
import {
  Card,
  Stack,
  Container,
  Typography,
  Button,
  Divider,
  Modal,
  TextField,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import api from 'src/services/BaseService';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import { registerAllModules } from 'handsontable/registry';
import moment from 'moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import towers from '../towers.json';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Services from 'src/services/Services';
import { useLocalization } from 'src/context/LocalizationContext';

registerAllModules();
pdfMake.vfs = pdfFonts.pdfMake.vfs;

export default function FullFeaturedCrudGrid() {
  const calculatedHeight = window.innerHeight - 250;
  const { language } = useLocalization();
  const mall = useSelector((state) => state.customerReducer);

  const hotRef = React.useRef(null);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const initialRows = React.useMemo(
    () =>
      Array.from({ length: 50 }, (_, index) => ({
        id: index + 1,
        barcode: '',
        name: '',
        corporation: '',
        tower: '',
        no: '',
        shipmentFirm: '',
        direction: '',
        packageCount: '',
        corporationID: '',
      })),
    []
  );

  const columnHeaders = React.useMemo(
    () => [
      language.commons.column.no,
      language.commons.column.barcode,
      language.commons.column.person,
      language.commons.column.company,
      language.commons.column.tower,
      language.commons.column.flat,
      language.commons.column.ship_company,
      language.commons.direction,
      language.commons.quantity,
      language.commons.column.firm_id,
    ],
    [language]
  );

  const [cargoList, setCargoList] = React.useState([]);
  const [towerList, setTowerList] = React.useState([]);
  const [existingDbBarcodes, setExistingDbBarcodes] = React.useState([]);

  React.useEffect(() => {
    if (mall?.customer?.id === '61973122632916808660') {
      setTowerList([
        'R1',
        'R2',
        'R3',
        'T0',
        'T1',
        'T2',
        'T3',
        'T4',
        'T5',
        'OF0',
        'OF1',
        'OF2',
        'OF3',
        'OF4',
        'OF5',
      ]);
    } else {
      setTowerList([]);
    }
  }, [mall]);

  React.useEffect(() => {
    const parsedUser = JSON.parse(localStorage.getItem('user'));
    let cargoFirms = [];

    Services.GetCargoFirms(parsedUser.token)
      .then((res) => {
        if (res.data.status) {
          res.data.result.forEach((cargoFirm) =>
            cargoFirms.push(cargoFirm.title)
          );
          const sortedCargoFirms = [
            ...cargoFirms.filter((cargoFirm) => cargoFirm !== 'Diğer'),
            cargoFirms.find((cargoFirm) => cargoFirm === 'Diğer'),
          ].filter(Boolean);

          if (!isMountedRef.current) return;
          setCargoList(sortedCargoFirms);
        }
      })
      .catch((err) => console.log(err));
  }, []);

  React.useEffect(() => {
    if (mall?.customer?.id) {
      api
        .post('posts/get', {
          type: 'post-office',
          mallID: mall.customer.id,
          status: [5],
          limit: 10000,
        })
        .then((response) => {
          if (!isMountedRef.current) return;

          if (response.data.status && response.data.result) {
            const barcodes = response.data.result
              .map((item) => item.barcode)
              .filter((b) => b);
            setExistingDbBarcodes(barcodes);
          }
        })
        .catch((err) => console.error('Barkod listesi çekilemedi', err));
    }
  }, [mall]);

  const directionList = React.useMemo(() => ['Gelen Kargo', 'Giden Kargo'], []);

  const [customerList, setCustomerList] = React.useState([]);
  const [offices, setOffices] = React.useState([]);

  // Bu iki state şu an UI’da readOnly bağlanmıyor; sizin kodunuzda da comment’liydi.
  const [IsTowerSelectReadOnly, setIsTowerSelectReadOnly] =
    React.useState(true);
  const [IsOfficeSelectReadOnly, setIsOfficeSelectReadOnly] =
    React.useState(true);

  const columnSettings = React.useMemo(
    () => [
      { readOnly: true, width: 50, className: 'htCenter htMiddle' }, // NO
      { strict: false, className: 'htCenter htMiddle', width: 150 }, // Barkod
      { strict: false, className: 'htCenter htMiddle', width: 200 }, // İsim
      {
        type: 'autocomplete',
        strict: true,
        className: 'htCenter htMiddle',
        width: 150,
        source: customerList?.map((option) => option?.value),
      }, // Firma
      {
        type: 'autocomplete',
        // readOnly: IsTowerSelectReadOnly,
        strict: true,
        className: 'htCenter htMiddle',
        width: 50,
        source: towerList,
      }, // Kule
      {
        type: 'autocomplete',
        // readOnly: IsOfficeSelectReadOnly,
        strict: true,
        className: 'htCenter htMiddle',
        width: 100,
        source: offices,
      }, // Daire
      {
        className: 'htCenter htMiddle',
        width: 100,
        type: 'autocomplete',
        strict: true,
        source: cargoList,
      }, // Kargo Firması
      {
        className: 'htCenter htMiddle',
        width: 102,
        type: 'autocomplete',
        strict: true,
        source: directionList,
      }, // Yön
      { strict: false, className: 'htCenter htMiddle', width: 50 }, // Adet
      {
        readOnly: true,
        strict: false,
        visibleRows: 50,
        className: 'htCenter htMiddle',
        width: 200,
      }, // Firma ID
    ],
    [customerList, towerList, offices, cargoList, directionList]
  );

  const [isSuccessModalOpen, setSuccessModalOpen] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [tableData, setTableData] = React.useState(undefined);
  const [failedRowsCount, setFailedRowsCount] = React.useState(undefined);

  const [controlModalOpen, setControlModalOpen] = React.useState(false);
  const [smsModal, setSmsModal] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [muhaberatID, setMuhaberatID] = React.useState('');

  // İlk render’da tabloyu initial data ile doldur
  const initialHotData = React.useMemo(
    () =>
      initialRows.map((row) => [
        row.id,
        row.barcode,
        row.name,
        row.corporation,
        row.tower,
        row.no,
        row.shipmentFirm,
        '',
        row.packageCount,
        row.corporationID,
      ]),
    [initialRows]
  );

  // tableData undefined ise HotTable’a initialHotData verelim
  const hotData = tableData ?? initialHotData;

  const createPDF = () => {
    const hotInstance = hotRef.current?.hotInstance;
    if (!hotInstance || hotInstance.isDestroyed) return;

    const table = hotInstance.getData();

    const nonEmptyRows = table
      .filter((row) => {
        const slicedRow = row.slice(1, -1);
        return slicedRow.every((cell, index) => {
          if (index === 3 || index === 4) return true; // tower/office boş kalabilir
          return cell !== '' && cell !== null;
        });
      })
      .map((row) => {
        const corporational = row[3];
        const cargoFirmName = row[6];

        let direction = '';
        if (row[7] === 'Giden Kargo') direction = 'CustomerToMallLogistics';
        else if (row[7] === 'Gelen Kargo')
          direction = 'MallLogisticsToCustomer';

        const matchingCustomer = customerList.find(
          (item) => item.value === corporational
        );
        const matchingCargo = cargoList.find((item) => item === cargoFirmName);

        if (matchingCustomer && matchingCargo) {
          return [
            row[0].toString(),
            row[1],
            row[2],
            row[3],
            row[4],
            row[5]?.toString?.() ?? '',
            row[6],
            row[7],
            row[8],
            row[9],
          ];
        }
        return null;
      })
      .filter(Boolean);

    if (nonEmptyRows?.length > 0) {
      const headerText = `Zorlu Center - ${moment().format(
        'DD.MM.YYYY HH:mm'
      )} -  ${nonEmptyRows.length} Adet`;

      const docDefinition = {
        content: [
          { text: headerText, style: 'header' },
          {
            table: {
              headerRows: 1,
              widths: [
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
                'auto',
              ],
              body: [columnHeaders, ...nonEmptyRows],
              alignment: 'center',
              margin: ['auto'],
            },
          },
          { text: '\nTeslim Alan:', margin: [0, 10, 0, 0] },
          { text: 'imza:', margin: [0, 10, 0, 0] },
        ],
        styles: {
          header: {
            fontSize: 18,
            bold: true,
            margin: [0, 0, 0, 10],
            alignment: 'center',
          },
        },
      };

      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      pdfDocGenerator.open();
      pdfDocGenerator.getBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Muhaberat_${moment().format('DD-MM-YYYY_HH:mm')}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      });
    } else {
      openSuccessModal('Lütfen boş alan bırakmayınız', true);
    }

    setControlModalOpen(false);
  };

  function removeTowerAndOffice(data) {
    const updatedData = data.map((item) => {
      if (!item.companyName?.toLowerCase()?.includes('residance')) {
        return { ...item, tower: '', office: '' };
      }
      return item;
    });
    return updatedData;
  }

  const translationMap = {
    'Barcode/Delivery Code must be unique':
      'Barkod veya teslimat kodu benzersiz olmalıdır.',
    'Invalid email address': 'Geçersiz e-posta adresi',
  };
  function translateMessage(message) {
    return translationMap[message] || message;
  }

  const openSuccessModal = (message, count) => {
    setSuccessMessage(message);
    setFailedRowsCount(count);
    setSuccessModalOpen(true);
  };

  const handleCreate = () => {
    const hotInstance = hotRef.current?.hotInstance;
    if (!hotInstance || hotInstance.isDestroyed) return;

    const table = hotInstance.getData();

    let nonEmptyRows = table
      .filter((row) => {
        const slicedRow = row.slice(1, -1);
        return slicedRow.every((cell, index) => {
          if (index === 3 || index === 4) return true; // tower/office boş olabilir
          return cell !== '' && cell !== null;
        });
      })
      .map((row) => {
        const customerName = row[2];
        const corporational = row[3];
        const cargoFirmName = row[6];

        let direction = '';
        if (row[7] === 'Giden Kargo') direction = 'CustomerToMallLogistics';
        else if (row[7] === 'Gelen Kargo')
          direction = 'MallLogisticsToCustomer';

        const matchingCustomer = customerList.find(
          (item) => item.value === corporational
        );
        const matchingCargo = cargoList.find((item) => item === cargoFirmName);
        if (matchingCustomer && matchingCargo) {
          return {
            barcode: row[1],
            shipmentFirm: cargoFirmName,
            isMultipleTransportation: false,
            isOnlyCreateJob: true,
            type:
              matchingCustomer.type === 'office'
                ? 'personal-post'
                : 'post-office',
            isPhysicalBarcode: true,
            status: 5,
            direction,
            transportationTool: 'Koli / Kargo',
            customer: matchingCustomer.id,
            from:
              row[7] === 'Giden Kargo'
                ? matchingCustomer.locationID
                : muhaberatID,
            to:
              row[7] === 'Giden Kargo'
                ? muhaberatID
                : matchingCustomer.locationID,
            sentToEasyPoint: muhaberatID,
            customerName,
            index: row[0],
            companyName: row[3],
            tower: row[4],
            office: row[5],
            packageCount: row[8],
            requestedInventoryCount: row[8],
            orderInventory: row[8],
          };
        }
        return null;
      })
      .filter(Boolean);

    nonEmptyRows = removeTowerAndOffice(nonEmptyRows);

    if (nonEmptyRows?.length > 0) {
      api
        .post('mall-logistics/create', nonEmptyRows)
        .then((response) => {
          const numJobsCreated = nonEmptyRows.length;

          if (response.data.status === true) {
            const data = hotInstance.getData();

            const newData = data.map((row) => {
              const matchingRow = nonEmptyRows.find(
                (nonEmptyRow) => nonEmptyRow?.index === row[0]
              );
              if (matchingRow)
                return [row[0], '', '', '', '', '', '', '', '', ''];
              return row;
            });

            const filledRowsCount = newData.filter((row) =>
              row.slice(1).some((cell) => cell !== '')
            ).length;

            openSuccessModal(
              `Toplam ${numJobsCreated} adet iş oluştu.`,
              filledRowsCount
            );
            setTableData(newData);
          } else {
            const erorMessage = response.data.message;
            const translatedMessage = translateMessage(erorMessage);
            openSuccessModal(translatedMessage, true);
          }
        })
        .catch(() => {
          // Mevcut kodunuzda catch boştu; aynı bırakıldı
        });
    } else {
      openSuccessModal('Lütfen boş alan bırakmayınız', true);
    }
  };

  // Müşteri listesi + muhaberatID çek
  React.useEffect(() => {
    const mallID = localStorage.getItem('mallID');

    api
      .post('flow/get-malls-branches', { mallID })
      .then((response) => {
        if (!isMountedRef.current) return;

        if (response.data.status) {
          const result = response.data.result;
          const processedDataList = result?.map((item) => ({
            value: item.title,
            id: item.branchID,
            corporationID: item.corporationID ? item.corporationID : '-',
            locationID: item.locations[0].locationID,
            type: item.locations[0].type,
          }));
          setCustomerList(processedDataList);
        } else {
          toast.error(
            'Müşteri listesi çekilirken hata oluştu, bağlantınızı kontrol ediniz.'
          );
          setCustomerList([]);
        }
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        toast.error(
          'Müşteri listesi çekilirken hata oluştu, bağlantınızı kontrol ediniz.'
        );
        setCustomerList([]);
      });

    api
      .post('/malls/get-malls', { withOffices: true })
      .then((res) => {
        if (!isMountedRef.current) return;

        if (res.data.status) {
          const foundMall = res.data.result.find((item) => item.id === mallID);
          if (foundMall) {
            const foundOffice = foundMall.offices.find(
              (item) => item.isMuhaberat === '1'
            );
            setMuhaberatID(foundOffice?.id || '');
          }
        } else {
          toast.error(
            "AVM'nizin bilgileri çekilirken hata oluştu, bağlantınızı kontrol ediniz."
          );
          setMuhaberatID('');
        }
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        toast.error(
          "AVM'nizin bilgileri çekilirken hata oluştu, bağlantınızı kontrol ediniz."
        );
        setMuhaberatID('');
      });
  }, []);

  // IMPORTANT: removeHook hatasının kök nedeni olan addHook/removeHook EFFECT KALDIRILDI.
  // Hook artık HotTable'a afterChange prop'u ile veriliyor.

  const onAfterChange = React.useCallback(
    (changes, source) => {
      if (source === 'loadData') return;

      const hotInstance = hotRef.current?.hotInstance;
      if (!hotInstance || hotInstance.isDestroyed) return;

      const dataToSet = [];
      const allData = hotInstance.getData();

      changes?.forEach(([row, col, prevValue, newValue]) => {
        const currentValue = hotInstance.getDataAtCell(row, col);

        // Barkod duplicate kontrolü
        if (col === 1 && newValue && newValue !== '') {
          const duplicateRowIndex = allData.findIndex(
            (item, index) => index !== row && item[1] === newValue
          );
          if (duplicateRowIndex !== -1) {
            const conflictRowNumber = duplicateRowIndex + 1;
            toast.error(
              `Bu Barkod numarası ${conflictRowNumber} nolu satırdaki barkod ile aynıdır`
            );
            dataToSet.push([row, 1, '']);
          } else if (existingDbBarcodes.includes(newValue)) {
            toast.error(
              'Bu barkod numarası sistemde zaten kayıtlıdır! (Şubede)'
            );
            dataToSet.push([row, 1, '']);
          }
        }

        // Firma (col 3) seçilince
        else if (col === 3 && newValue !== '') {
          dataToSet.push([row, 8, 1]);

          const directionColumn = hotInstance.getDataAtCell(row, 7);
          if (!directionColumn) {
            dataToSet.push([row, 7, 'Gelen Kargo']);
          }

          if (newValue !== null) {
            if (newValue?.includes('Residance')) {
              setIsTowerSelectReadOnly(false);
            } else {
              const towerColumn = hotInstance.getDataAtCell(row, 4);
              if (newValue !== prevValue && towerColumn !== '') {
                dataToSet.push([row, 4, '']);
              }
            }
          } else {
            setIsTowerSelectReadOnly(true);
            setIsOfficeSelectReadOnly(true);
            setOffices([]);
            dataToSet.push([row, 4, '']);
            dataToSet.push([row, 5, '']);
          }

          const matchingCustomer = customerList?.find(
            (item) => item.value === newValue
          );
          if (matchingCustomer) {
            dataToSet.push([row, 9, matchingCustomer?.corporationID]);
          }
        } else if (col === 3) {
          if (newValue !== currentValue) {
            dataToSet.push([row, 9, '']);
          }
        }

        // Kule (col 4) seçilince
        else if (
          col === 4 &&
          newValue !== '' &&
          newValue !== null &&
          newValue
        ) {
          if (newValue !== currentValue) {
            dataToSet.push([row, 5, '']);
          }
          setIsOfficeSelectReadOnly(false);
          setOffices([]);

          let type = 'A';
          if (mall?.customer?.id === '61973122632916808660') {
            if (['R1', 'R2', 'R3'].includes(newValue)) {
              type = 'kule';
              setOffices(towers[type][newValue]);
            } else if (
              ['T0', 'T1', 'T2', 'T3', 'T4', 'T5'].includes(newValue)
            ) {
              type = 'teras';
              setOffices(towers[type][newValue]);
            } else if (
              ['OF0', 'OF1', 'OF2', 'OF3', 'OF4', 'OF5'].includes(newValue)
            ) {
              type = 'ofis';
              setOffices(towers[type][newValue]);
            }
          }
        } else if ((newValue === '' || newValue === null) && col === 4) {
          setIsOfficeSelectReadOnly(true);
          setOffices([]);
          dataToSet.push([row, 5, '']);
        }

        // İsim (col 2) uppercase
        else if (col === 2) {
          if (newValue && newValue !== newValue.toUpperCase()) {
            const updatedValue = newValue.toUpperCase();
            dataToSet.push([row, 2, updatedValue]);
          }
        }
      });

      if (dataToSet.length > 0) {
        hotInstance.setDataAtCell(dataToSet);
      }
    },
    [customerList, cargoList, existingDbBarcodes, mall?.customer?.id]
  );

  return (
    <>
      <Helmet>
        <title>{language.muhaberat_entry.titles.site_title}</title>
      </Helmet>

      <Stack style={{ paddingInline: '25px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {language.muhaberat_entry.titles.title}
          </Typography>

          <div>
            <Button
              onClick={handleCreate}
              variant="contained"
              endIcon={<SendIcon />}
              sx={{ marginRight: '12px' }}
            >
              {language.muhaberat_entry.titles.create_work}
            </Button>

            <Button
              onClick={() => setControlModalOpen(true)}
              variant="contained"
              endIcon={<SendIcon />}
            >
              {language.muhaberat_entry.titles.ktf}
            </Button>
          </div>
        </Stack>

        <Card>
          <Scrollbar>
            <Box sx={{ width: '100%' }}>
              <HotTable
                ref={hotRef}
                rowHeights={35}
                licenseKey="non-commercial-and-evaluation"
                data={hotData}
                persistentState={true}
                colHeaders={columnHeaders}
                columns={columnSettings}
                stretchH="all"
                height={calculatedHeight}
                afterChange={onAfterChange}
              />
            </Box>
          </Scrollbar>
        </Card>

        <Modal
          open={isSuccessModalOpen}
          onClose={() => {
            setSuccessModalOpen(false);
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 400,
              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              p: 4,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            {successMessage.includes('Toplam işe oluştu') ? (
              <CheckCircleIcon sx={{ color: 'green', fontSize: 64 }} />
            ) : (
              <WarningOutlined sx={{ color: 'orange', fontSize: 64 }} />
            )}

            <Typography variant="h6">{successMessage}</Typography>

            {failedRowsCount > 0 && (
              <>
                <Divider sx={{ my: 2, backgroundColor: 'gray' }} />
                <Typography variant="h6">
                  Uyarı: {failedRowsCount} satırınızın verileri doldurulmadığı
                  için bu satırlarda iş oluşturulamadı.
                </Typography>
              </>
            )}

            <Button
              onClick={() => {
                setSuccessModalOpen(false);
              }}
            >
              {language.commons.okay}
            </Button>
          </Box>
        </Modal>

        <Modal
          open={controlModalOpen}
          onClose={() => setControlModalOpen(false)}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 400,
              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              p: 4,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6">
              {language.muhaberat_entry.titles.delivery}
            </Typography>
            <Button onClick={() => setSmsModal(true)}>
              {language.muhaberat_entry.titles.sms}
            </Button>
            <Button
              onClick={() => {
                handleCreate();
                createPDF();
              }}
            >
              {language.muhaberat_entry.titles.document}
            </Button>
          </Box>
        </Modal>

        <Modal open={smsModal} onClose={() => setSmsModal(false)}>
          <Box
            sx={{
              position: 'absolute',
              width: 400,
              bgcolor: 'background.paper',
              border: '2px solid #000',
              boxShadow: 24,
              p: 4,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6">
              {language.muhaberat_entry.titles.enter_code}
            </Typography>
            <TextField
              label={language.muhaberat_entry.titles.code}
              variant="outlined"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              sx={{ my: 2 }}
            />
            <div>
              <Button>{language.muhaberat_entry.titles.take_delivery}</Button>
            </div>
          </Box>
        </Modal>
      </Stack>
    </>
  );
}
